import React, { useCallback, useEffect, useState } from "react";
import { IconTag, IconPlus, IconX } from "@tabler/icons-react";
import {
  getTagsByPipeline,
  createTag,
  updateTag,
} from "../../../../screens/Tags/services/TagServices";

const PALETTE = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7",
  "#DDA0DD", "#FFB347", "#F8C8DC", "#98FB98", "#FF9FF3",
];

const getRandomColor = () => PALETTE[Math.floor(Math.random() * PALETTE.length)];

/**
 * Lista e gerencia as tags preset de um pipeline.
 *
 * Modelo no BE: cada Tag tem campo M2M `pipelines`. Quando uma tag tem
 * `pipeline.id` em `pipelines`, ela aparece como sugestão para contatos
 * desse pipeline (via /api/v1/tags/?pipeline=<id> ou search?pipeline=<id>).
 *
 * Aqui o operador pode:
 * - Ver as tags preset desse pipeline
 * - Criar uma tag nova já vinculada a este pipeline
 * - Remover uma tag do pipeline (a tag continua existindo, só deixa de
 *   aparecer como preset deste pipeline)
 */
const PipelineTagsPresetSection = ({ pipelineId, onClose }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(getRandomColor);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTagsByPipeline(pipelineId);
      const all = data?.results || data || [];
      // BE retorna tag account-wide + presets desse pipeline.
      // Aqui só queremos as que de fato têm este pipeline em pipelines/pipeline_ids
      const presets = all.filter((t) => {
        const ids = t.pipeline_ids || t.pipelines || [];
        return ids.includes(pipelineId);
      });
      setTags(presets);
    } catch (err) {
      setError("Falha ao carregar tags do pipeline.");
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setError(null);
    setSaving(true);
    try {
      await createTag({
        name,
        color: newColor,
        category: "contact",
        pipeline_ids: [pipelineId],
      });
      setNewName("");
      setNewColor(getRandomColor());
      fetchTags();
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Erro ao criar tag.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (tag) => {
    if (saving) return;
    const currentIds = tag.pipeline_ids || tag.pipelines || [];
    const remaining = currentIds.filter((id) => id !== pipelineId);
    setSaving(true);
    try {
      await updateTag(tag.id, { pipeline_ids: remaining });
      fetchTags();
    } catch (err) {
      setError("Erro ao remover tag do pipeline.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 6,
        padding: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#334155" }}>
          <IconTag size={14} />
          Tags deste pipeline
        </div>
        {onClose && (
          <button
            className="crm-btn crm-btn--sm"
            onClick={onClose}
            title="Fechar"
            style={{ background: "none", border: "none", padding: 2, cursor: "pointer" }}
          >
            <IconX size={14} />
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize: 12, color: "#64748b" }}>Carregando...</div>}

      {!loading && tags.length === 0 && (
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
          Nenhuma tag definida. Crie abaixo — vão aparecer como sugestão ao taggear contatos deste pipeline.
        </div>
      )}

      {!loading && tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {tags.map((tag) => (
            <span
              key={tag.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 8px",
                borderRadius: 12,
                background: tag.color || "#94a3b8",
                color: "#fff",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {tag.name}
              <button
                onClick={() => handleRemove(tag)}
                disabled={saving}
                title="Remover do pipeline"
                style={{
                  background: "rgba(255,255,255,0.25)",
                  border: "none",
                  borderRadius: "50%",
                  width: 16,
                  height: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <IconX size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          placeholder="nome da nova tag"
          disabled={saving}
          style={{
            flex: 1,
            padding: "4px 8px",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            fontSize: 13,
          }}
        />
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          disabled={saving}
          title="Cor"
          style={{ width: 30, height: 30, border: "none", padding: 0, cursor: "pointer" }}
        />
        <button
          className="crm-btn crm-btn--sm crm-btn--primary"
          onClick={handleCreate}
          disabled={!newName.trim() || saving}
          style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          <IconPlus size={12} /> Adicionar
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
};

export default PipelineTagsPresetSection;
